/**
 * MonitorSetupModal - Simple modal for creating monitors on existing datasets
 * Used by MonitorButton for datasets that already have a webset.
 */
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';
import { FreeTierLock, CreditPill } from '../ui';
import { apiClient } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { CREDIT_COSTS } from '../../constants/credits';
import {
  ScheduleConfig,
  ScheduleFrequency,
  scheduleToCron,
} from '../../types/monitoring';
import {
  TIMEZONES,
  DAYS_OF_WEEK,
  MAX_RESULTS_OPTIONS,
  MAX_RESULTS_LIMIT,
} from '../../constants/scheduling';

interface MonitorSetupModalProps {
  tableId: string;
  tableName: string;
  onClose: () => void;
  onComplete: () => void;
}

export const MonitorSetupModal: React.FC<MonitorSetupModalProps> = ({
  tableId,
  tableName,
  onClose,
  onComplete,
}) => {
  const { showToast } = useToast();
  const { isFreeTier } = useSubscription();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [monitorName, setMonitorName] = useState(`${tableName} Monitor`);
  const [schedule, setSchedule] = useState<ScheduleConfig>({
    frequency: 'daily',
    hourOfDay: 8,
    daysOfWeek: ['mon'],
    timezone: 'Etc/UTC',
  });
  const [maxResults, setMaxResults] = useState(25);
  const [customMaxResults, setCustomMaxResults] = useState('');
  const [maxResultsError, setMaxResultsError] = useState<string | null>(null);

  const handleMaxResultsChange = (value: number | 'custom') => {
    if (value === 'custom') {
      setCustomMaxResults(String(maxResults));
      setMaxResults(-1); // Use -1 to indicate custom mode
      setMaxResultsError(null);
    } else {
      setMaxResults(value);
      setCustomMaxResults('');
      setMaxResultsError(null);
    }
  };

  const handleCustomMaxResultsChange = (value: string) => {
    setCustomMaxResults(value);
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 1) {
      setMaxResultsError('Must be at least 1');
    } else if (num > MAX_RESULTS_LIMIT) {
      setMaxResultsError(`Maximum is ${MAX_RESULTS_LIMIT}`);
    } else {
      setMaxResultsError(null);
      setMaxResults(num);
    }
  };

  const handleSubmit = async () => {
    if (!monitorName.trim()) {
      showToast('Please enter a monitor name', 'error');
      return;
    }

    const finalMaxResults = maxResults === -1 ? parseInt(customMaxResults, 10) : maxResults;
    if (isNaN(finalMaxResults) || finalMaxResults < 1 || finalMaxResults > MAX_RESULTS_LIMIT) {
      showToast('Invalid max results value', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.createMonitor({
        lead_table_id: tableId,
        name: monitorName.trim(),
        cron_expression: scheduleToCron(schedule),
        timezone: schedule.timezone,
        max_results: finalMaxResults,
        search_behavior: 'append',
      });

      showToast('Monitor created successfully', 'success');
      onComplete();
    } catch (error) {
      console.error('Failed to create monitor:', error);
      showToast('Failed to create monitor', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = isSubmitting || !monitorName.trim() || !!maxResultsError;

  if (isFreeTier) {
    const freeTierContent = (
      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] shadow-2xl overflow-y-auto">
          <div className="flex justify-between items-center px-6 py-5 border-b border-neutral-200">
            <h2 className="text-lg font-semibold text-neutral-900">Set Up Monitor</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-neutral-100 transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 text-neutral-500" />
            </button>
          </div>
          <div className="p-6 relative h-[40vh]">
            <FreeTierLock
              title="Upgrade to unlock Monitors"
              description="Subscribe to a paid plan to set up automated monitoring for your datasets."
            />
          </div>
        </div>
      </div>
    );
    return ReactDOM.createPortal(freeTierContent, document.body);
  }

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-auto shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-5 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-neutral-900">Set Up Monitor</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-neutral-100 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-sm text-neutral-500 mb-6">
            Configure automatic monitoring for <strong className="text-neutral-700">{tableName}</strong>
          </p>

          {/* Monitor Name */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Monitor Name
            </label>
            <input
              type="text"
              value={monitorName}
              onChange={(e) => setMonitorName(e.target.value)}
              placeholder="Enter monitor name"
              className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
            />
          </div>

          {/* Frequency */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Frequency
            </label>
            <div className="flex gap-2">
              {(['daily', 'weekly', 'monthly'] as ScheduleFrequency[]).map((freq) => (
                <button
                  key={freq}
                  onClick={() => setSchedule({ ...schedule, frequency: freq })}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    schedule.frequency === freq
                      ? 'bg-primary-600 text-white'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                  }`}
                >
                  {freq.charAt(0).toUpperCase() + freq.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Day of Week (for weekly) */}
          {schedule.frequency === 'weekly' && (
            <div className="mb-5">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Day of Week
              </label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map((day) => (
                  <button
                    key={day.value}
                    onClick={() => {
                      const current = schedule.daysOfWeek;
                      const updated = current.includes(day.value)
                        ? current.filter((d) => d !== day.value)
                        : [...current, day.value];
                      setSchedule({ ...schedule, daysOfWeek: updated.length > 0 ? updated : [day.value] });
                    }}
                    className={`px-3 py-1.5 rounded-md text-13 font-medium transition-colors ${
                      schedule.daysOfWeek.includes(day.value)
                        ? 'bg-primary-600 text-white'
                        : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                    }`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Time and Timezone */}
          <div className="flex gap-4 mb-5">
            <div className="flex-1">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Time
              </label>
              <select
                value={schedule.hourOfDay}
                onChange={(e) => setSchedule({ ...schedule, hourOfDay: parseInt(e.target.value) })}
                className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg text-sm bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-600"
              >
                {Array.from({ length: 24 }, (_, i) => {
                  const hour = i === 0 ? 12 : i > 12 ? i - 12 : i;
                  const period = i < 12 ? 'AM' : 'PM';
                  return (
                    <option key={i} value={i}>
                      {hour}:00 {period}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Timezone
              </label>
              <select
                value={schedule.timezone}
                onChange={(e) => setSchedule({ ...schedule, timezone: e.target.value })}
                className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg text-sm bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-600"
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Max Results */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Max Results Per Run
            </label>
            <div className="flex gap-2 flex-wrap">
              {MAX_RESULTS_OPTIONS.map((num) => (
                <button
                  key={num}
                  onClick={() => handleMaxResultsChange(num)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    maxResults === num
                      ? 'bg-primary-600 text-white'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                  }`}
                >
                  {num}
                </button>
              ))}
              <button
                onClick={() => handleMaxResultsChange('custom')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  maxResults === -1
                    ? 'bg-primary-600 text-white'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                Custom
              </button>
            </div>
            {maxResults === -1 && (
              <div className="mt-3">
                <input
                  type="number"
                  value={customMaxResults}
                  onChange={(e) => handleCustomMaxResultsChange(e.target.value)}
                  placeholder="Enter custom amount"
                  min={1}
                  max={MAX_RESULTS_LIMIT}
                  className={`w-28 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 ${
                    maxResultsError
                      ? 'border border-error-600 focus:ring-error-600'
                      : 'border border-neutral-300 focus:ring-primary-600'
                  }`}
                />
                {maxResultsError && (
                  <p className="text-13 text-error-600 mt-1">{maxResultsError}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-200">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-5 py-2.5 rounded-lg text-sm font-medium border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
              isSubmitDisabled
                ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                : 'bg-primary-600 text-white hover:bg-primary-700'
            }`}
          >
            {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {isSubmitting ? 'Creating...' : 'Create Monitor'}
            {!isSubmitting && (
              <CreditPill cost={CREDIT_COSTS.LEAD_GENERATION} perLead />
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default MonitorSetupModal;
